import { StatCard } from '../ui';
import { Prospect } from './DealCard';
import { formatMoney, shortMoney, stageEnteredMs } from './meta';

interface SalesStatsProps {
  /** Open deals all time, closed deals narrowed to the selected timeframe. */
  prospects: Prospect[];
}

/**
 * KPI strip — Pipeline Value, Won, Avg Deal Size, In Proposal — over the cards the
 * page is currently showing, so the tiles and the board agree. Only Won follows the
 * timeframe; the open-pipeline tiles are all time.
 */
export default function SalesStats({ prospects }: SalesStatsProps) {
  const open = prospects.filter((p) => !p.stage.startsWith('closed'));
  const pipelineValue = open.reduce((sum, p) => sum + p.deal_value, 0);

  const won = prospects.filter((p) => p.stage === 'closed_won');
  const wonValue = won.reduce((sum, p) => sum + p.deal_value, 0);
  const latestWon = [...won].sort((a, b) => stageEnteredMs(b) - stageEnteredMs(a))[0];

  const proposal = prospects.filter((p) => p.stage === 'proposal');
  const proposalValue = proposal.reduce((sum, p) => sum + p.deal_value, 0);

  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-[16px]"
      style={{ marginBottom: 'var(--space-6)' }}
    >
      <StatCard
        label="Pipeline Value"
        value={formatMoney(pipelineValue)}
        hint={`${open.length} open deal${open.length === 1 ? '' : 's'}`}
      />
      <StatCard
        label="Won"
        value={formatMoney(wonValue)}
        delta={won.length > 0 ? `+${won.length}` : undefined}
        hint={latestWon ? latestWon.company_name : undefined}
      />
      <StatCard
        label="Avg Deal Size"
        value={open.length > 0 ? shortMoney(Math.round(pipelineValue / open.length)) : '—'}
        hint="open pipeline"
      />
      <StatCard
        label="In Proposal"
        value={String(proposal.length)}
        hint={proposal.length > 0 ? `${shortMoney(proposalValue)} at stake` : undefined}
      />
    </div>
  );
}
